import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import FieldRow from 'components/FormElements/FieldRow';
import TextField from 'components/FormElements/AppTextInput';
import { FirmBankDetailsSelector } from 'components/FirmBankDetailsSelector';
import { createFirmEditRoute } from 'adminConstants/routes';
import RecordView from 'components/RecordView';
import CkEditor from 'components/CkEditor';
import { Control, Controller } from 'react-hook-form';
import { TrancheFormInput } from '../useTrancheForm';

const useStyles = makeStyles()(() => ({
  marginLeftBtn: {
    marginLeft: 10,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

type Props = {
  control: Control<TrancheFormInput>;
  handleCancel: () => void;
  handleClickBank: () => void;
  id: string;
  firmId: string;
  buttonDisabled?: boolean;
  formDisabled?: boolean;
};

const BankDetails: React.FC<Props> = ({
  control,
  handleCancel,
  handleClickBank,
  id,
  firmId,
  buttonDisabled = false,
  formDisabled = false,
}) => {
  const { classes } = useStyles();

  return (
    <RecordView>
      <FieldRow
        title="Select payment account"
        centerTitle
        tooltipText={
          <span>
            To add a new bank account, navigate to the{' '}
            <em>Fund bank account details</em> section of your{' '}
            <Link to={createFirmEditRoute(firmId)}>My Firm</Link> page.
          </span>
        }
      >
        <Controller
          control={control}
          name="firmBankDetailId"
          render={({ field, fieldState }) => (
            <>
              <FirmBankDetailsSelector
                firmId={firmId}
                onChange={(value) => field.onChange(value)}
                placeholder="Select payment account"
                currentValue={field.value}
                disabled={formDisabled}
              />
              {fieldState.error?.message && (
                <span style={{ color: 'red' }}>{fieldState.error.message}</span>
              )}
            </>
          )}
        />
      </FieldRow>

      <FieldRow
        title="Custom reference"
        tooltipText="By default, the system automatically generates a unique bank transfer reference for each investor. If you want investors to follow a specific format instead, please input this here for investors to see alongside your bank account details. Please note that if any text is input here, it will override the system's automatic payment reference creation feature."
        centerTitle
      >
        <Controller
          control={control}
          name="customBankTransferReference"
          render={({ field }) => (
            <TextField
              name="customBankTransferReference"
              fullWidth
              disabled={formDisabled}
              onChange={(event) => field.onChange(event.target.value)}
              value={field.value}
              placeholder="e.g. AV-FirstNameLastName"
              variant="outlined"
            />
          )}
        />
      </FieldRow>

      <FieldRow
        title="Payment helper text"
        informationText="This helper text appears on the bank transfer payment page, alongside bank details."
        topTitle
      >
        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <CkEditor
              placeholder="(Optional) Enter up to 30-35 words in this field if you wish to provide additional payment instructions or context, for example how to pay by cheque, or what your custodian's name is. The system automatically creates payment references for each investor, so please do not add references here."
              onChange={(value: string) => field.onChange(value)}
              value={field.value}
              disabled={formDisabled}
              onBlur={(value: string) => field.onChange(value)}
              extraLines={1}
            />
          )}
        />
      </FieldRow>

      {!formDisabled && (
        <FieldRow>
          <div className={classes.buttonContainer}>
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              className={classes.marginLeftBtn}
              variant="contained"
              color="primary"
              disabled={buttonDisabled}
              onClick={handleClickBank}
            >
              {id ? 'Update' : 'Save'}
            </Button>
          </div>
        </FieldRow>
      )}
    </RecordView>
  );
};

export default BankDetails;
